import React, { useEffect, useMemo, useState } from "react";
import {
  fetchPTOBankBalance,
  fetchPTOBankBalanceForWorker,
  fetchPTORequestsForWorker,
  fetchPTORequestsForWorkerAdmin,
} from "@service/ptoService";
import { IPTOBankBalanceDTO } from "@interfaces/IPTOBankBalanceDTO";
import HeadlessDialog from "@components/Dialog";
import PTOBankBalance from "./PTOBankBalance";
import PTORequestsTable from "./PTORequestsTable";
import { LuFileClock } from "react-icons/lu";
import { useTranslation } from "react-i18next";

interface IPTOBankBalanceDialogProps {
  workerId?: number;
  contractorName: string;
  contractorId: number | string;
  isOpen: boolean;
  onClose: () => void;
  isAdminView?: boolean;
}

export const PTOBankBalanceDialog: React.FC<IPTOBankBalanceDialogProps> = ({
  workerId,
  contractorName,
  contractorId,
  isOpen,
  onClose,
  isAdminView = false,
}) => {
  const { t } = useTranslation();
  const startYearFilter = useMemo(() => new Date().getFullYear() - 1, []);
  const [ptoBalance, setPtoBalance] = useState<IPTOBankBalanceDTO | null>(null);
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [showRequestsTable, setShowRequestsTable] = useState(false);
  const [requestType, setRequestType] = useState<"vacation" | "sick" | "all">(
    "all"
  );
  const [vacationRequestCount, setVacationRequestCount] = useState(0);
  const [sickRequestCount, setSickRequestCount] = useState(0);
  const [totalRequestCount, setTotalRequestCount] = useState(0);

  const handleViewRequests = (type: "vacation" | "sick" | "all") => {
    setRequestType(type);
    setShowRequestsTable(true);
  };

  // Fetch PTO requests and count them when dialog opens
  useEffect(() => {
    if (isOpen && workerId) {
      const fetchFunction = isAdminView
        ? fetchPTORequestsForWorkerAdmin(workerId)
        : fetchPTORequestsForWorker(workerId, startYearFilter);

      fetchFunction
        .then((data) => {
          const requests = data.requests || [];
          const vacationCount = requests.filter(
            (req: any) => req.request_type === "vacation"
          ).length;
          const sickCount = requests.filter(
            (req: any) => req.request_type === "sick"
          ).length;

          setVacationRequestCount(vacationCount);
          setSickRequestCount(sickCount);
          setTotalRequestCount(requests.length);
        })
        .catch((err) => {
          console.error("Failed to fetch PTO request counts:", err);
          // Set to 0 on error
          setVacationRequestCount(0);
          setSickRequestCount(0);
          setTotalRequestCount(0);
        });
    }
  }, [isOpen, workerId, isAdminView, startYearFilter]);

  // Fetch PTO balance when dialog opens
  useEffect(() => {
    if (isOpen) {
      setBalanceLoading(true);
      setBalanceError(null);
      setPtoBalance(null);

      const fetchBalance =
        isAdminView && workerId
          ? fetchPTOBankBalanceForWorker(workerId)
          : fetchPTOBankBalance();

      fetchBalance
        .then((data) => {
          setPtoBalance(data);
          setBalanceLoading(false);
        })
        .catch((err) => {
          console.error("Failed to fetch PTO balance:", err);
          const errorMsg =
            err.response?.data?.error ||
            err.message ||
            t("ptoBankBalance.errors.failedToLoad");

          // Provide more helpful error messages
          let userFriendlyError = errorMsg;
          if (
            errorMsg.includes("Worker has no start date") ||
            errorMsg.includes("start_date")
          ) {
            userFriendlyError = t("ptoBankBalance.errors.noStartDate");
          } else if (
            errorMsg.includes("not found") ||
            errorMsg.includes("404")
          ) {
            userFriendlyError = t("ptoBankBalance.errors.notInitialized");
          } else if (
            errorMsg.includes("not authorized") ||
            errorMsg.includes("403")
          ) {
            userFriendlyError = t("ptoBankBalance.errors.notAuthorized");
          } else if (errorMsg.includes("500")) {
            userFriendlyError = t("ptoBankBalance.errors.needsInitialization");
          }

          setBalanceError(userFriendlyError);
          setBalanceLoading(false);
        });
    }
  }, [isOpen, workerId, isAdminView, t]);

  return (
    <>
      <HeadlessDialog
        title={
          <div className="tw-flex tw-items-center tw-gap-2">
            <LuFileClock className="tw-text-primary-light" size={24} />
            <span>{t("ptoBankBalance.title")}</span>
          </div>
        }
        description=""
        isOpen={isOpen}
        setIsOpen={onClose}
        onClose={onClose}
        buttonText={t("ptoBankBalance.close")}
        size="tw-w-[100vw] md:tw-max-w-[50vw]"
      >
        <div className="tw-px-0 tw-pb-2">
          {/* Contractor Information */}
          <div className="tw-mb-4 tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-border tw-border-gray-200">
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <div>
                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-2">
                  {contractorName}
                </h3>
                <p className="tw-text-gray-600">
                  {t("ptoBankBalance.contractorId")}:{" "}
                  <span className="tw-font-medium">{contractorId}</span>
                </p>
              </div>
              <div className="tw-flex tw-justify-end tw-items-center">
                <img
                  src="/ptoImage.jpg"
                  alt={t("ptoBankBalance.imageAlt")}
                  className="tw-rounded-lg tw-shadow-md tw-max-w-full tw-h-auto tw-max-h-48 tw-object-cover"
                />
              </div>
            </div>
          </div>

          {/* PTO Bank Balance Display */}
          {ptoBalance && (
            <PTOBankBalance
              balance={ptoBalance}
              isLoading={balanceLoading}
              error={balanceError}
              onViewRequests={workerId ? handleViewRequests : undefined}
              vacationRequestCount={vacationRequestCount}
              sickRequestCount={sickRequestCount}
              totalRequestCount={totalRequestCount}
            />
          )}
          {balanceLoading && !ptoBalance && (
            <PTOBankBalance
              balance={{} as IPTOBankBalanceDTO}
              isLoading={true}
            />
          )}
          {balanceError && !ptoBalance && (
            <PTOBankBalance
              balance={{} as IPTOBankBalanceDTO}
              error={balanceError}
            />
          )}

          {/* Additional Information */}
          <div className="tw-mt-4 tw-text-xs tw-text-gray-500 tw-border-t tw-pt-4">
            <p>{t("ptoBankBalance.info.calculation")}</p>
            <p className="tw-mt-1">{t("ptoBankBalance.info.contactHR")}</p>
          </div>
        </div>
      </HeadlessDialog>

      {/* PTO Requests Table */}
      {typeof workerId === "number" && showRequestsTable && (
        <PTORequestsTable
          workerId={workerId}
          requestType={requestType}
          isOpen={showRequestsTable}
          onClose={() => setShowRequestsTable(false)}
          isAdminView={isAdminView}
        />
      )}
    </>
  );
};

export default PTOBankBalanceDialog;
